import { useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import { useAppContext } from "~/contexts/appContext";
import { BulkImportDataVaultExtensionFieldValuesDocument } from "~/graphql/supergraphHooks";
import useUploadFilesToS3 from "~/hooks/uploadFilesToS3/useUploadFilesToS3";
import { generateUniqueFileName } from "../utils/utils";

export type UseBulkUploadOptions = {
  onUploadSuccess?: () => void;
  onClose: () => void;
};

export type UseBulkUploadReturn = {
  uploadFileAndQueueTask: (file: File) => Promise<void>;
  isUploading: boolean;
};

export const useBulkUpload = ({
  onUploadSuccess,
  onClose,
}: UseBulkUploadOptions): UseBulkUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const { user } = useAppContext();
  const { uploadFilesToS3 } = useUploadFilesToS3();

  const [bulkImportMutation] = useMutation(BulkImportDataVaultExtensionFieldValuesDocument);

  const uploadFileAndQueueTask = useCallback(
    async (file: File) => {
      setIsUploading(true);

      try {
        const uniqueFileName = generateUniqueFileName(file.name, user.fullName);
        const renamedFile = new File([file], uniqueFileName, {
          type: file.type,
        });

        const uploadResults = await uploadFilesToS3([renamedFile]);

        if (!uploadResults || uploadResults.length === 0) {
          throw new Error("Failed to upload file to S3");
        }

        const s3Key = uploadResults[0].objectKey;

        const result = await bulkImportMutation({
          variables: {
            input: {
              s3Key,
            },
          },
        });

        const jobId = result.data?.bulkImportDataVaultExtensionFieldValues?.jobId;
        if (jobId) {
          toast.success(
            `File uploaded successfully. Processing job ${jobId} will begin shortly.`,
          );
          onUploadSuccess?.();
          onClose();
        } else {
          throw new Error("Failed to register bulk import job");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to upload file. Please try again.";
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadFilesToS3, bulkImportMutation, user.fullName, onUploadSuccess, onClose],
  );

  return {
    uploadFileAndQueueTask,
    isUploading,
  };
};
